import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { ToolTipSettings } from '../tooltip/tooltip.util';
import { NOTIFICATIONS, NotificationsService } from 'src/app/core/service/notifications.service';
import { ModalDeleteListComponent } from './modal-delete-list/modal-delete-list.component';
import {
  ModalButtons,
  ModalContainerComponent, SpinnerProps,
} from "src/app/domain/shared/component/modal-container/modal-container.component";
import { AllIconsComponent } from "src/app/domain/shared/component/all-icons/all-icons.component";
import { TooltipComponent } from "src/app/domain/shared/component/tooltip/tooltip.component";
import { InputComponent } from "../inputs/input-component/input.component";
import { STANDARD_TEXT_LIST_FOR_MODAL_DELETE } from "./modal-dialog-var.service";

@Component({
  selector: 'app-modal-dialog',
  templateUrl: './modal-dialog.component.html',
  styleUrls: ['./modal-dialog.component.scss'],
  imports: [
    ModalContainerComponent,
    ModalDeleteListComponent,
    AllIconsComponent,
    InputComponent,
    TooltipComponent,
  ],
})
export class ModalDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly TEXT_FOR_TOOLTIP = 'Copy to clipboard';
  private _destroyStream: Subject<void> = new Subject<void>();
  private notificationsService = inject(NotificationsService);

  protected readonly THINK_TWICE = 'thinktwice';
  protected readonly ID_FOR_COPY = 'thinktwice-copy';

  @Input() header: string;
  @Input() innerModal = false;
  @Input() mainText: string[];
  @Input() addAdditionalText = false;
  @Input() showIUnderstand = true;
  @Input() hideAllText = false;
  // show or hide div as whitespace
  @Input() showWhiteSpace = true;
  // if true standard text list would be displayed
  @Input() useStandardList = true;
  @Input() customUnderstandString: string;
  // show or hide text input
  @Input() showInput = true;
  @Input() modalButtons: ModalButtons;

  /**
   * isConfirmed is emitted when the user clicks either on the "confirm" button or on the "cancel" button.
   * The value of the event is true if the user clicked on the "confirm" button, false otherwise.
   */
  @Output() isConfirmed = new EventEmitter<boolean>();

  @ViewChild('input') focus: ElementRef;

  spinnerProps: SpinnerProps = {
    isSpinnerActive: false,
    notActiveOnApply: true,
  };

  toolTipSettings: ToolTipSettings = {
    show: false,
    title: '',
  };

  isThinkTwiceInvalid = false;
  thinkTwiceInput: string;

  close(): void {
    this.isConfirmed.emit(false);
  }

  ngOnInit(): void {
    this.thinkTwiceInput = '';
    if (!this.modalButtons) {
      this.modalButtons = {
        dismiss: 'Cancel',
        apply: 'Delete',
      };
    }
    if (this.addAdditionalText) {
      this.mainText = this.mainText.concat(STANDARD_TEXT_LIST_FOR_MODAL_DELETE);
    }
    if (!this.showInput) {
      this.thinkTwiceInput = this.THINK_TWICE;
    }
    // for old ver if description added it would be concat with standard text list
    if (!this.mainText?.length) {
      this.mainText = [].concat(STANDARD_TEXT_LIST_FOR_MODAL_DELETE);
    }
    if (this.hideAllText) {
      this.mainText = undefined;
    }
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  ngAfterViewInit(): void {
    this.focus?.nativeElement?.focus();
  }

  activateToolTip(): void {
    this.toolTipSettings.title = this.TEXT_FOR_TOOLTIP;
    this.toolTipSettings.show = true;
    this.toolTipSettings.coordinates = document.getElementById(this.ID_FOR_COPY)?.getBoundingClientRect();
  }

  async copyToClipboard(): Promise<void> {
    await navigator.clipboard.writeText(this.THINK_TWICE);
    this.notificationsService.success(NOTIFICATIONS.COPIED_NO_TITLE);
  }

  checkThinkTwicePassword(): void {
    if (this.thinkTwiceInput === this.THINK_TWICE) {
      this.isConfirmed.emit(true);
    } else {
      this.isThinkTwiceInvalid = true;
    }
  }
}
